import { useTranslation } from 'next-i18next';
import { useCallback, useEffect } from 'react';
import { ButtonV2, Text } from '@bridebook/ui';
import PrePromptIcon from '@bridebook/web/components/app/push-pre-prompt/pre-prompt-icon';
import { requestPushNotificationPermissions } from '@bridebook/web/lib/mobile-app/actions';
import { PN_OPT_IN_STORAGE_KEY } from '@bridebook/web/lib/mobile-app/constants';
import {
  getPreferences,
  getPrefNumber,
} from '@bridebook/web/lib/mobile-app/utils/preferences-wrapper';
import { PushNotificationOptInInteraction } from '@bridebook/web/lib/types';
import {
  togglePushNotificationOptIn,
  togglePushNotificationOptInAnalytics,
} from '@bridebook/web/lib/ui/actions';
import {
  getIsPushNotificationOptInVisible,
  getPushNotificationOptInVariant,
} from '@bridebook/web/lib/ui/selectors';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { getPartners } from '@bridebook/web/lib/weddings/selectors';
import PrePromptModal from './push-pre-prompt/pre-prompt-modal';

const CordovaPushNotificationsOptIn = () => {
  const { t } = useTranslation('mobileApp');
  const pushNotificationOptInShown = useSelector(getIsPushNotificationOptInVisible);
  const pushNotificationOptInVariant = useSelector(getPushNotificationOptInVariant);
  const [name] = useSelector(getPartners);
  const dispatch = useDispatch();

  const registerPN = useCallback(() => {
    dispatch(
      togglePushNotificationOptInAnalytics({
        show: false,
        interactionType: 'accepted',
        popupVersion: pushNotificationOptInVariant,
      }),
    );
    dispatch(requestPushNotificationPermissions());
  }, [pushNotificationOptInVariant, dispatch]);

  const closePushNotificationOptIn = useCallback(
    (option: PushNotificationOptInInteraction) => {
      dispatch(
        togglePushNotificationOptInAnalytics({
          show: false,
          interactionType: option,
          popupVersion: pushNotificationOptInVariant,
        }),
      );
      dispatch(togglePushNotificationOptIn(false));
    },
    [pushNotificationOptInVariant, dispatch],
  );

  useEffect(() => {
    const setLocalStorage = async () => {
      if (pushNotificationOptInShown) {
        let currentValue: number = await getPrefNumber({
          key: PN_OPT_IN_STORAGE_KEY,
        });

        getPreferences().set({
          key: PN_OPT_IN_STORAGE_KEY,
          value: (++currentValue).toString(),
        });
      }
    };

    setLocalStorage();
  }, [pushNotificationOptInShown]);

  const getModalTitle = () => {
    /** Use adjusted copies when user's name is not provided since the "Partner" fallback doesn't fit here */
    if (!name.trim()) {
      switch (pushNotificationOptInVariant) {
        case 'budget':
          return t('pushNotificationsOptInModal.variantTitle.budgetWithoutName');
        case 'enquiry':
          return t('pushNotificationsOptInModal.variantTitle.enquiryWithoutName');
        case 'home':
          return t('pushNotificationsOptInModal.variantTitle.homeWithoutName');
        case 'inbox':
          return t('pushNotificationsOptInModal.variantTitle.inboxWithoutName');
        case 'articles':
          return t('pushNotificationsOptInModal.variantTitle.articlesWithoutName');
        case 'shortlist':
          return t('pushNotificationsOptInModal.variantTitle.shortlistWithoutName');
        case 'tasks':
          return t('pushNotificationsOptInModal.variantTitle.tasksWithoutName');
        default:
          return t('pushNotificationsOptInModal.variantTitle.genericWithoutName');
      }
    }

    switch (pushNotificationOptInVariant) {
      case 'budget':
        return t('pushNotificationsOptInModal.variantTitle.budget', { name });
      case 'enquiry':
        return t('pushNotificationsOptInModal.variantTitle.enquiry', { name });
      case 'home':
        return t('pushNotificationsOptInModal.variantTitle.home', { name });
      case 'inbox':
        return t('pushNotificationsOptInModal.variantTitle.inbox', { name });
      case 'articles':
        return t('pushNotificationsOptInModal.variantTitle.articles', {
          name,
        });
      case 'shortlist':
        return t('pushNotificationsOptInModal.variantTitle.shortlist', {
          name,
        });
      case 'tasks':
        return t('pushNotificationsOptInModal.variantTitle.tasks', {
          name,
        });
      default:
        return t('pushNotificationsOptInModal.variantTitle.generic', { name });
    }
  };

  const getModalDescription = () => {
    switch (pushNotificationOptInVariant) {
      case 'budget':
        return t('pushNotificationsOptInModal.variantDescription.budget');
      case 'enquiry':
        return t('pushNotificationsOptInModal.variantDescription.enquiry');
      case 'home':
        return t('pushNotificationsOptInModal.variantDescription.home');
      case 'inbox':
        return t('pushNotificationsOptInModal.variantDescription.inbox');
      case 'articles':
        return t('pushNotificationsOptInModal.variantDescription.articles');
      case 'shortlist':
        return t('pushNotificationsOptInModal.variantDescription.shortlist');
      case 'tasks':
        return t('pushNotificationsOptInModal.variantDescription.tasks');
      default:
        return t('pushNotificationsOptInModal.variantDescription.generic');
    }
  };

  const getConfirmButtonText = () => {
    switch (pushNotificationOptInVariant) {
      case 'generic': {
        return t('pushNotificationsOptInModal.variantConfirmButton.keepUpdated');
      }
      default: {
        return t('pushNotificationsOptInModal.variantConfirmButton.turnOnNotifications');
      }
    }
  };

  return (
    <PrePromptModal
      show={pushNotificationOptInShown}
      onClose={() => closePushNotificationOptIn('closed')}>
      <PrePromptModal.Body>
        <PrePromptModal.InnerContent>
          <PrePromptModal.Icon>
            <PrePromptIcon />
          </PrePromptModal.Icon>

          <PrePromptModal.Title>{getModalTitle()}</PrePromptModal.Title>

          <PrePromptModal.Description>{getModalDescription()}</PrePromptModal.Description>
        </PrePromptModal.InnerContent>
        <PrePromptModal.Buttons>
          <ButtonV2 onClick={registerPN}>
            <Text>{getConfirmButtonText()}</Text>
          </ButtonV2>
          <ButtonV2 variant="link" onClick={() => closePushNotificationOptIn('dismissed')}>
            {t('pushNotificationsOptInModal.maybeLater')}
          </ButtonV2>
        </PrePromptModal.Buttons>
      </PrePromptModal.Body>
    </PrePromptModal>
  );
};

export default CordovaPushNotificationsOptIn;
